import React from 'react';
import { useTranslation } from 'react-i18next';
import { Field, FieldArray } from 'formik';
import type { FormikValues } from 'formik';
import { Button, LabelWithHelp, UserSelectField, FieldHelp } from '../../../components';
import { User } from '../../../models';
import { RootState, useAppSelector } from '../../../redux';

interface SprintsFormProps {};

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

function SprintsForm(props: SprintsFormProps) {
    const { t } = useTranslation();
    const { user } = useAppSelector((state: RootState) => state.auth);

    if (user?.role !== User.ROLE_ADMIN) {
        return (
            <div className="flex flex-col">
                <h2 className="mt-4 mb-1 text-base font-semibold text-lg text-gray-900 dark:text-gray-100">
                    {t('settings.sprints')}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">{t('settings.sprints_admin_only')}</p>
            </div>
        );
    }

    const membersFilter = (candidate: User, values: FormikValues, index: number) => {
        const ids = (values?.sprints?.default_resources || [])
            .map((r: AnyObj, i: number) => (i === index ? null : (r.member?._id || r.member)))
            .filter(Boolean);
        return !ids.includes(candidate._id);
    };

    return (
        <div className="flex flex-col space-y-8">
            <div>
                <h2 className="mt-4 mb-1 text-base font-semibold text-lg text-gray-900 dark:text-gray-100">
                    {t('settings.sprints')}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('settings.sprints_intro')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <LabelWithHelp htmlFor="sprints.start.weekday" help={t('settings.help.sprints_window') || ''}>
                        {t('settings.sprints_start')}
                    </LabelWithHelp>
                    <div className="mt-2 flex gap-2">
                        <Field
                            as="select"
                            name="sprints.start.weekday"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                        >
                            {WEEKDAYS.map(day => (
                                <option key={`start-${day}`} value={day}>
                                    {t(`settings.weekday_${day}`)}
                                </option>
                            ))}
                        </Field>
                        <Field
                            type="time"
                            name="sprints.start.time"
                            className="block w-40 rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                        />
                    </div>
                </div>
                <div>
                    <LabelWithHelp htmlFor="sprints.end.weekday" help={t('settings.help.sprints_window') || ''}>
                        {t('settings.sprints_end')}
                    </LabelWithHelp>
                    <div className="mt-2 flex gap-2">
                        <Field
                            as="select"
                            name="sprints.end.weekday"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                        >
                            {WEEKDAYS.map(day => (
                                <option key={`end-${day}`} value={day}>
                                    {t(`settings.weekday_${day}`)}
                                </option>
                            ))}
                        </Field>
                        <Field
                            type="time"
                            name="sprints.end.time"
                            className="block w-40 rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                        />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <LabelWithHelp htmlFor="sprints.timezone" help={t('settings.help.sprints_timezone') || ''}>
                        {t('settings.sprints_timezone')}
                    </LabelWithHelp>
                    <div className="mt-2">
                        <Field
                            type="text"
                            name="sprints.timezone"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                            placeholder="Europe/Zurich"
                        />
                    </div>
                </div>
                <div>
                    <LabelWithHelp htmlFor="sprints.horizon" help={t('settings.help.sprints_horizon') || ''}>
                        {t('settings.sprints_horizon')}
                    </LabelWithHelp>
                    <div className="mt-2">
                        <Field
                            type="number"
                            name="sprints.horizon"
                            min={1}
                            max={52}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                        />
                    </div>
                </div>
            </div>

            <div>
                <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 inline-flex items-center gap-1">
                    {t('settings.sprints_default_resources')}
                    <FieldHelp content={t('settings.help.sprints_default_resources') || ''} />
                </h3>
                <FieldArray name="sprints.default_resources">
                    {({ remove, push, form }) => {
                        const resources = form.values?.sprints?.default_resources || [];
                        return (
                            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                                {resources.map((resource: AnyObj, i: number) => (
                                    <div key={i} className="border rounded p-3 bg-white dark:bg-gray-800">
                                        <UserSelectField
                                            name={`sprints.default_resources[${i}].member`}
                                            placeholder={t('sprints.choose_a_member') || ''}
                                            roles={[User.ROLE_ADMIN, User.ROLE_USER]}
                                            filter={(u: User) => membersFilter(u, form.values, i)}
                                        />
                                        <div className="mt-2 flex items-center gap-2">
                                            <Field
                                                type="number"
                                                name={`sprints.default_resources[${i}].availability.percentage`}
                                                className="block w-full rounded-md border-gray-300 dark:border-gray-600 py-1 text-gray-900 dark:text-gray-100 sm:text-sm"
                                                min={0}
                                                max={100}
                                            />
                                            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">%</span>
                                            <Button
                                                type="button"
                                                onClick={() => remove(i)}
                                                title={t('common.delete')}
                                                color="danger"
                                                icon="delete"
                                                small
                                            />
                                        </div>
                                    </div>
                                ))}
                                <div className="flex items-center">
                                    <Button
                                        type="button"
                                        onClick={() => push({
                                            member: '',
                                            availability: { percentage: 100 }
                                        })}
                                        title={t('sprints.add_member')}
                                        color="navigate"
                                        icon="plus"
                                        small
                                    />
                                </div>
                            </div>
                        );
                    }}
                </FieldArray>
            </div>
        </div>
    );
}

export default SprintsForm;
